import { normalizePhoneNumber } from '../../lib/phone-number';
import { MAX_NAME_CODE_POINTS, normalizeFullName } from '../../lib/name-answers';
import { useFileCollection } from './useFileCollection';
import { useCallback, useEffect, useRef, useState } from 'react';
import { liveSessionWorkerUrl } from '../../lib/worker-urls';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import { isEmailAnswer, isWebsiteAnswer, parseRespondentConversationState, type CalendarControl, type ConversationErrorCode, type ConversationRequest, type RespondentConversationState, type RespondentField } from '../../lib/multimodal-contract';
import type { InlineAttachment } from '../../lib/file-attachment';
import type { QuestionStructuredValue } from '../../lib/question-blocks';

/** Unsent text remains private and anchored even when a direct selection advances. */
export interface ConversationDraft { text: string; fieldId: string | null; label: string }

/** A submitted message awaiting acknowledgement, separate from accepted state. */
export interface PendingConversationInput { id: string; fieldId: string; text: string }

/** Stable option IDs or the exact rating value, never display-label inference. */
export interface ControlAnswer { attachmentIds?: string[]; value?: number; optionIds?: string[]; otherText?: string; structuredValue?: QuestionStructuredValue; calendar?: CalendarControl }

/** Response credentials supplied by the owning entry and recovery flow. */
interface ConversationOptions { roomId: string; responseId: string | null; responseToken: string | null; enabled?: boolean }

const errorCopy: Record<ConversationErrorCode, string> = {
  invalid_input: 'Check your answer and send it again.',
  stale_revision: 'The conversation moved on. Review your answer before sending.',
  unavailable: 'hollie cannot continue right now.',
  not_authorized: 'This conversation is unavailable. Contact the person who shared the link.',
  not_ready: 'Resolve the remaining question before finishing.',
  input_not_allowed: 'Answer this question on screen.',
  call_required: 'Join the video call to continue.',
  signing_confirmation_required: 'Confirm your signature before finishing.',
  duplicate_email: 'This email has already been used.',
};

/** Validate literal field values before submitting; the server enforces the same authored limits. */
export function validateDirectAnswer(field: RespondentField, value: string | number): string | null {
  if (field.type === 'number') {
    if (typeof value !== 'number' || !Number.isFinite(value)) return 'Enter a number.';
    if ((field.min !== undefined && value < field.min) || (field.max !== undefined && value > field.max)) {
      if (field.min !== undefined && field.max !== undefined) return `Enter a number from ${field.min} to ${field.max}.`;
      return field.min !== undefined ? `Enter ${field.min} or more.` : `Enter ${field.max} or less.`;
    }
    if (field.step !== undefined && field.step > 0) {
      const steps = (value - (field.min ?? 0)) / field.step;
      if (Math.abs(steps - Math.round(steps)) > 1e-8) return `Use increments of ${field.step}.`;
    }
    return null;
  }
  if (field.type === 'name') {
    // A name is kept as typed: the shared normalizer only trims, so any rejection is empty, oversized or unprintable input.
    if (typeof value === 'string' && normalizeFullName(value)) return null;
    return typeof value === 'string' && [...value.trim()].length > MAX_NAME_CODE_POINTS
      ? `Use ${MAX_NAME_CODE_POINTS} characters or fewer.` : 'Enter your name.';
  }
  if (typeof value !== 'string' || !value.trim()) return 'Enter your answer.';
  if ((field.type === 'email' || (field.type === 'calendar_booking' && field.calendar?.input === 'email'))
    && !isEmailAnswer(value)) return 'Enter a valid email address.';
  if (field.type === 'phone' && !normalizePhoneNumber(value)) return 'Enter a valid phone number with its country code.';
  if (field.type === 'short_text' && field.textFormat === 'url' && !isWebsiteAnswer(value)) return 'Enter a valid website URL.';
  if (field.minLength !== undefined && value.length < field.minLength) return `Use at least ${field.minLength} characters.`;
  if (field.maxLength !== undefined && value.length > field.maxLength) return `Use ${field.maxLength} characters or fewer.`;
  return null;
}

/** Shared state for the main thread and call panel, independent of media lifetime. */
export function useRespondentConversation({ roomId, responseId, responseToken, enabled = true }: ConversationOptions) {
  const [state, setState] = useState<RespondentConversationState | null>(null);
  const stateRef = useRef(state);
  const hasFileFields = state?.activeField?.type === 'file_upload' || state?.answers.some(answer => answer.field?.type === 'file_upload');
  const fileCollection = useFileCollection(enabled && hasFileFields ? responseId : null, responseToken);
  const [draft, updateDraft] = useState<ConversationDraft>({ text: '', fieldId: null, label: '' });
  const [editingField, setEditingField] = useState<RespondentField | null>(null);
  const repairTarget = useRef<string | null>(null);
  const [amendmentDrafts, setAmendmentDrafts] = useState<Record<string, ConversationDraft>>({});
  const draftVersion = useRef(0);
  const submittedDrafts = useRef(new Map<string, { target: string | null; version: number; text: string }>());
  const currentDraft = editingField ? amendmentDrafts[editingField.fieldId]
    ?? { text: '', fieldId: editingField.fieldId, label: editingField.label } : draft;
  const [pending, setPending] = useState(false);
  const [pendingInput, setPendingInput] = useState<PendingConversationInput | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [canRetry, setCanRetry] = useState(false);
  const [selections, setSelections] = useState<Record<string, string[]>>({});
  const [structuredValues, setStructuredValues] = useState<Record<string, QuestionStructuredValue | undefined>>({});
  const scrollPosition = useRef(0);
  const busy = useRef(false);
  const retryRequest = useRef<ConversationRequest | null>(null);
  const rebaseFileRetry = useRef(false);

  /** A definitive revision conflict may be retried only for the same unresolved file question. */
  const fileRetryStillApplies = useCallback((event: ConversationRequest): event is Extract<ConversationRequest, { type: 'input'; kind: 'files' }> => {
    const latest = stateRef.current;
    return event.type === 'input' && event.kind === 'files' && latest?.responseId === responseId
      && latest.status !== 'submitted' && (latest.activeField?.fieldId === event.fieldId && latest.activeField.type === 'file_upload'
        || repairTarget.current === event.fieldId && latest.answers.some(answer => answer.fieldId === event.fieldId
          && answer.status === 'failed' && answer.field?.type === 'file_upload'));
  }, [responseId]);

  const acceptState = useCallback((incoming: RespondentConversationState) => {
    if (incoming.responseId !== responseId || incoming.revision < (stateRef.current?.revision ?? -1)) return;
    stateRef.current = incoming;
    setState(incoming);
  }, [responseId]);

  const send = useCallback(async (event: ConversationRequest): Promise<boolean> => {
    if (!enabled || !responseId || !responseToken || busy.current) return false;
    // Resolve an uncertain answer with its original event before allowing
    // another mutation to replace the retry receipt.
    if ((retryRequest.current?.type === 'input' || retryRequest.current?.type === 'submit') && event !== retryRequest.current) return false;
    busy.current = true;
    setPending(true);
    const bookingEmail = event.type === 'input' && event.kind === 'control' && event.calendar?.action === 'book' ? event.calendar.attendeeEmail : undefined;
    if (event.type === 'input' && (event.kind !== 'control' && event.kind !== 'files' || bookingEmail)) {
      setPendingInput({ id: event.eventId, fieldId: event.fieldId,
        text: event.kind === 'control' ? bookingEmail! : event.kind === 'answer' ? String(event.value) : event.kind === 'files' ? '' : event.text.trim() || (event.attachment ? `Attached: ${event.attachment.fileName}` : '') });
    }
    setError(null);
    setCanRetry(false);
    // The event whose outcome is unknown if its acknowledgement is lost.
    let inFlight = event;
    try {
      const query = new URLSearchParams({ room_id: roomId, interview_data_id: responseId });
      /** Post one event and accept any authoritative state its reply carries. */
      const post = async (request: ConversationRequest) => {
        const requestAbort = new AbortController();
        // Keep the same event available for explicit retry if its acknowledgement never arrives.
        // Keep allowing older workers to finish a typed turn during a rolling upgrade.
        const timeoutMs = request.type === 'input' && request.kind === 'text' ? 90_000 : 60_000;
        const acknowledgementTimeout = setTimeout(() => requestAbort.abort(), timeoutMs);
        try {
          const response = await fetch(`${liveSessionWorkerUrl()}/conversation?${query}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${responseToken}` },
            body: JSON.stringify(request),
            signal: requestAbort.signal,
          });
          const payload: unknown = await response.json();
          if (!payload || typeof payload !== 'object') throw new Error('Invalid conversation acknowledgement');
          const result = payload as Record<string, unknown>;
          const next = parseRespondentConversationState(result.state);
          if (next) acceptState(next);
          return { response, result, next };
        } finally {
          clearTimeout(acknowledgementTimeout);
        }
      };
      const first = await post(event);
      // hollie can show the same field first and move the revision while this
      // browser confirms it is visible. Re-send exactly once, as a fresh event at
      // the newest accepted revision, and only while that field is still active.
      const latest = stateRef.current;
      const freshPresentation: ConversationRequest | null = event.type === 'presented' && first.result.ok !== true
        && first.response.status === 409 && first.result.code === 'stale_revision' && first.next?.responseId === responseId
        && !!latest && latest.revision > event.expectedRevision && latest.activeField?.fieldId === event.fieldId
        ? { ...event, eventId: crypto.randomUUID(), expectedRevision: latest.revision } : null;
      if (freshPresentation) inFlight = freshPresentation;
      // The event whose acknowledgement decides this call.
      const settled = freshPresentation ?? event;
      const { response, result, next } = freshPresentation ? await post(freshPresentation) : first;
      if (result.ok !== true) {
        const code = typeof result.code === 'string' && Object.hasOwn(errorCopy, result.code)
          ? result.code as ConversationErrorCode : null;
        if (!code) throw new Error('Invalid conversation outcome');
        setError(settled.type === 'presented' && code === 'stale_revision' ? 'Could not confirm this question is visible.'
          : settled.type === 'submit' && settled.signatureConfirmation && code === 'stale_revision'
          ? 'The conversation changed. Review it and confirm your signature again.' : errorCopy[code]);
        const staleFiles = code === 'stale_revision' && settled.type === 'input' && settled.kind === 'files';
        // Missing or foreign state is not authority to change a request whose outcome is uncertain.
        if (staleFiles && (!next || next.responseId !== responseId || next.revision <= settled.expectedRevision)) {
          throw new Error('Invalid file revision acknowledgement');
        }
        rebaseFileRetry.current = staleFiles && fileRetryStillApplies(settled);
        const retryable = code === 'unavailable' || rebaseFileRetry.current;
        retryRequest.current = retryable ? settled : null;
        setCanRetry(retryable);
        return false;
      }
      if (!response.ok || !next || next.responseId !== responseId) throw new Error('Invalid conversation state');
      retryRequest.current = null; rebaseFileRetry.current = false;
      const repairing = (event.type === 'input' || event.type === 'skip') && repairTarget.current === event.fieldId;
      const repaired = repairing && stateRef.current?.answers.some(answer => answer.fieldId === event.fieldId
        && (answer.status === 'answered' || (answer.status === 'skipped' && answer.field?.required === false)));
      if (repairing && !repaired) {
        // An input acknowledgement is not proof that clarification resolved the answer.
        submittedDrafts.current.delete(event.eventId);
        setError('This answer still needs clarification.');
        return false;
      }
      if (event.type === 'input' && event.kind !== 'control' && event.kind !== 'files') {
        const submittedDraft = submittedDrafts.current.get(event.eventId);
        const submittedText = submittedDraft?.text ?? (event.kind === 'answer' ? String(event.value) : event.text);
        // Only a restored retry draft can be cleared here. New keystrokes have
        // their own identity, even if their text matches the submitted message.
        if (submittedDraft?.version === draftVersion.current) {
          if (submittedDraft.target) {
            const target = submittedDraft.target;
            setAmendmentDrafts(previous => previous[target]?.text === submittedText
              ? { ...previous, [target]: { ...previous[target], text: '' } } : previous);
          } else {
            updateDraft(current => current.text === submittedText && current.fieldId === event.fieldId
              ? { text: '', fieldId: null, label: '' } : current);
          }
        }
        submittedDrafts.current.delete(event.eventId);
      }
      if (repaired && (event.type === 'input' || event.type === 'skip')) {
        repairTarget.current = null;
        setAmendmentDrafts(previous => { const next = { ...previous }; delete next[event.fieldId]; return next; });
        setSelections(previous => { const next = { ...previous }; delete next[event.fieldId]; return next; });
        setStructuredValues(previous => { const next = { ...previous }; delete next[event.fieldId]; return next; });
        setEditingField(null);
      }
      return true;
    } catch {
      // Never send user text, credentials or dependency payloads to telemetry.
      captureException(new Error('Conversation request was not acknowledged'), 'useRespondentConversation.send', { roomId, responseId, operation: event.type });
      retryRequest.current = inFlight; rebaseFileRetry.current = false;
      setCanRetry(true);
      setError(event.type === 'bootstrap' ? 'Could not open the conversation.'
        : event.type === 'submit' && event.signatureConfirmation ? 'Could not confirm your signature. Retry to check whether it was saved.'
        : event.type === 'calendar_availability' ? 'Could not load available times.' : 'Could not confirm your answer.');
      return false;
    } finally {
      busy.current = false;
      setPending(false);
      if (retryRequest.current?.type !== 'input' || (retryRequest.current.kind === 'control' && !bookingEmail)) setPendingInput(null);
    }
  }, [acceptState, enabled, responseId, responseToken, roomId, fileRetryStillApplies]);

  useEffect(() => {
    if (enabled && responseId && responseToken) void send({ type: 'bootstrap' });
  }, [enabled, responseId, responseToken, send]);

  const setDraft = useCallback((text: string) => {
    draftVersion.current++;
    if (editingField) {
      // Acknowledging an older attempt must not close newly edited repair text.
      if (busy.current || (retryRequest.current?.type === 'input' && retryRequest.current.fieldId === editingField.fieldId)) {
        repairTarget.current = null;
      }
      setAmendmentDrafts(previous => ({ ...previous, [editingField.fieldId]: { text, fieldId: editingField.fieldId, label: editingField.label } }));
      return;
    }
    updateDraft(current => current.text
      ? { ...current, text }
      : { text, fieldId: stateRef.current?.activeField?.fieldId ?? null, label: stateRef.current?.activeField?.label ?? '' });
  }, [editingField]);
  const beginAmend = useCallback((fieldId: string) => {
    if (busy.current || retryRequest.current?.type === 'input' || retryRequest.current?.type === 'skip' || retryRequest.current?.type === 'submit') return;
    const answer = stateRef.current?.answers.find(answer => answer.fieldId === fieldId);
    if (answer?.field && stateRef.current?.status !== 'submitted') {
      setError(null);
      repairTarget.current = fieldId;
      const field = answer.field;
      setAmendmentDrafts(previous => previous[fieldId] ? previous : { ...previous, [fieldId]: {
        text: field.options?.length || field.rating ? '' : typeof answer.value === 'string' || typeof answer.value === 'number' ? String(answer.value) : '',
        fieldId, label: field.label,
      } });
      if (answer.optionIds) setSelections(previous => previous[fieldId] ? previous : { ...previous, [fieldId]: answer.optionIds! });
      if (answer.structuredValue !== undefined) setStructuredValues(previous => previous[fieldId] !== undefined
        ? previous : { ...previous, [fieldId]: answer.structuredValue });
      setEditingField(field);
    }
  }, []);
  const cancelAmend = useCallback(() => {
    // A sent change cannot be discarded while its authoritative outcome is unknown.
    if (!editingField || busy.current || retryRequest.current?.type === 'input' || retryRequest.current?.type === 'skip') return;
    const fieldId = editingField.fieldId;
    repairTarget.current = null;
    setAmendmentDrafts(previous => { const next = { ...previous }; delete next[fieldId]; return next; });
    setSelections(previous => { const next = { ...previous }; delete next[fieldId]; return next; });
    setStructuredValues(previous => { const next = { ...previous }; delete next[fieldId]; return next; });
    setError(null);
    setEditingField(null);
  }, [editingField]);
  const mutation = useCallback(() => ({ eventId: crypto.randomUUID(), expectedRevision: stateRef.current?.revision ?? 0 }), []);
  const sendDraft = useCallback(async (text = currentDraft.text, attachment: InlineAttachment | null = null) => {
    if (busy.current || retryRequest.current?.type === 'input' || (!text.trim() && !attachment)) return false;
    const fieldId = currentDraft.fieldId ?? stateRef.current?.activeField?.fieldId ?? null;
    if (!fieldId) return false;
    const field = editingField ?? (stateRef.current?.activeField?.fieldId === fieldId ? stateRef.current.activeField
      : stateRef.current?.answers.find(answer => answer.fieldId === fieldId)?.field);
    const editingSavedStory = editingField?.type === 'free_form_explore'
      && stateRef.current?.activeField?.fieldId !== editingField.fieldId;
    const direct = !attachment && !!field && (field.type === 'short_text' || field.type === 'email' || field.type === 'phone' || field.type === 'name'
      || (field.type === 'number' && !field.rating) || editingSavedStory);
    const value = field?.type === 'number' ? Number(text.trim()) : text.trim();
    if (field && (direct || (field.type === 'calendar_booking' && field.calendar?.input === 'email'))) {
      const validation = validateDirectAnswer(field, value);
      if (validation) { setError(validation); return false; }
    }
    const bookingSlot = field?.type === 'calendar_booking' && field.calendar?.input === 'email' ? field.calendar.selectedSlotId : undefined;
    const event: ConversationRequest = bookingSlot
      ? { type: 'input', ...mutation(), fieldId, kind: 'control', calendar: { action: 'book', slotId: bookingSlot, attendeeEmail: text.trim() } }
      : direct
      ? { type: 'input', ...mutation(), fieldId, kind: 'answer', value }
      : { type: 'input', ...mutation(), fieldId, kind: 'text', text, ...(attachment ? { attachment } : {}) };
    const target = editingField?.fieldId ?? null;
    if (target) repairTarget.current = target;
    const version = draftVersion.current;
    const submittedDraft = { ...currentDraft, text, fieldId };
    submittedDrafts.current.set(event.eventId, { target, version, text });
    if (target) setAmendmentDrafts(previous => ({ ...previous, [target]: { ...submittedDraft, text: '' } }));
    else updateDraft({ text: '', fieldId: null, label: '' });
    const accepted = await send(event);
    if (!accepted && version === draftVersion.current) {
      if (target) setAmendmentDrafts(previous => ({ ...previous, [target]: submittedDraft }));
      else updateDraft(submittedDraft);
    }
    return accepted;
  }, [currentDraft, editingField, mutation, send]);
  const answer = useCallback((value: ControlAnswer) => {
    const field = editingField ?? stateRef.current?.activeField;
    if (editingField) repairTarget.current = editingField.fieldId;
    if (!field) return Promise.resolve(false);
    if (value.attachmentIds) return send({ type: 'input', ...mutation(), fieldId: field.fieldId, kind: 'files', attachmentIds: value.attachmentIds });
    if (value.calendar?.action === 'availability') return send({ type: 'calendar_availability', fieldId: field.fieldId,
      timezone: value.calendar.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...(value.calendar.earliestDay ? { earliestDay: value.calendar.earliestDay } : {}) });
    return send({ type: 'input', ...mutation(), fieldId: field.fieldId, kind: 'control', ...value });
  }, [editingField, mutation, send]);
  const setStructuredValue = useCallback((fieldId: string, value: QuestionStructuredValue | undefined) => {
    setStructuredValues(previous => ({ ...previous, [fieldId]: value }));
  }, []);
  const presented = useCallback((fieldId: string) => {
    const previous = retryRequest.current;
    return send(previous?.type === 'presented' && previous.fieldId === fieldId
      ? previous : { type: 'presented', ...mutation(), fieldId });
  }, [mutation, send]);
  const skip = useCallback(() => {
    const field = editingField ?? stateRef.current?.activeField;
    if (!field || field.required || (editingField && !stateRef.current?.answers.some(answer => answer.fieldId === field.fieldId && answer.status === 'failed'))) {
      return Promise.resolve(false);
    }
    return send({ type: 'skip', ...mutation(), fieldId: field.fieldId });
  }, [editingField, mutation, send]);
  const submit = useCallback((signatureConfirmation?: Extract<ConversationRequest, { type: 'submit' }>['signatureConfirmation']) => {
    if (signatureConfirmation && signatureConfirmation.responseRevision !== stateRef.current?.revision) {
      setError('The conversation changed. Review it and confirm your signature again.');
      return Promise.resolve(false);
    }
    return send({ type: 'submit', ...mutation(), scope: 'full', ...(signatureConfirmation ? { signatureConfirmation } : {}) });
  }, [mutation, send]);
  const continueInformation = useCallback((fieldId: string) => send({ type: 'continue_information', ...mutation(), fieldId }), [mutation, send]);
  /** Explicit retries rebase only definitive file conflicts; unknown outcomes retain the exact request. */
  const retry = useCallback(() => {
    if (busy.current || !retryRequest.current) return Promise.resolve(false);
    if (rebaseFileRetry.current) {
      rebaseFileRetry.current = false;
      if (!fileRetryStillApplies(retryRequest.current)) {
        retryRequest.current = null; setCanRetry(false); setError(null);
        return Promise.resolve(false);
      }
      // Retain event identity for server receipts that may already contain an earlier processing step.
      retryRequest.current = { ...retryRequest.current, expectedRevision: stateRef.current!.revision };
    }
    return send(retryRequest.current);
  }, [send, fileRetryStillApplies]);
  const refresh = useCallback(() => send({ type: 'bootstrap' }), [send]);

  return { state, draft: currentDraft, setDraft, editingField, beginAmend, cancelAmend, pending, pendingInput, error, canRetry, sendDraft, answer, presented, skip, submit, continueInformation, retry, refresh,
    fileCollection, acceptState, selections, setSelections, structuredValues, setStructuredValue, scrollPosition };
}
